import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateConfig } from "../../../hooks/useSettings";
import { useToast } from "@finitless/shared/hooks";
import { handleApiError, handleApiSuccess } from "@finitless/shared/lib";
import {
  Button,
  Checkbox,
  Icon,
  IconButton,
  Input,
  NativeSelect,
} from "../../ui";

// Fields that should only accept numeric input
const NUMERIC_FIELDS = ["max_distance_pickup", "max_days_preorder"];

// Fields with character limits
const CHAR_LIMIT_FIELDS = {
  format_number_currency: 3,
};

const TECHNICAL_ENUM_PATTERN = /^[A-Z0-9]+(?:_[A-Z0-9]+)*$/;
const TECHNICAL_PATH_PATTERN = /^[A-Za-z0-9]+(?:[._][A-Za-z0-9]+)+$/;
const SHORT_ACRONYM_PATTERN = /^[A-Z]{2,3}$/;

const isTechnicalLabel = (value) => {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();

  if (!trimmed || trimmed.includes(" ")) return false;

  return (
    TECHNICAL_ENUM_PATTERN.test(trimmed) || TECHNICAL_PATH_PATTERN.test(trimmed)
  );
};

const humanizeTechnicalLabel = (value) => {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";

  const keySegment = trimmed.split(".").pop() || trimmed;

  if (SHORT_ACRONYM_PATTERN.test(keySegment)) {
    return keySegment;
  }

  const words = keySegment
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .split(" ")
    .filter(Boolean);

  if (words.length === 0) {
    return keySegment;
  }

  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const SettingsConfig = ({ config }) => {
  const { t, i18n } = useTranslation(["settings", "common"]);
  const [value, setValue] = useState(() => {
    if (config.value === null || config.value === undefined) return "";
    if (typeof config.value === "string") return config.value;
    return String(config.value);
  });
  const [isEditing, setIsEditing] = useState(false);
  const updateConfigMutation = useUpdateConfig();
  const toast = useToast();

  // Sync local value with config prop when it changes
  useEffect(() => {
    if (config.value === null || config.value === undefined) {
      setValue("");
    } else if (typeof config.value === "string") {
      setValue(config.value);
    } else {
      setValue(String(config.value));
    }
  }, [config.value]);

  const handleSave = async () => {
    // Validate multi-select configs require at least one selection
    if (config.type === 3) {
      const selectedValues = value
        ? value.split("|").filter((v) => v !== "")
        : [];
      if (selectedValues.length === 0) {
        toast.error(t("config.toasts.atLeastOneRequired"));
        return;
      }
    }

    try {
      await updateConfigMutation.mutateAsync({
        configId: config.id,
        key: config.key,
        value: value,
      });
      handleApiSuccess(t("config.toasts.updated"), toast, {
        title: t("states.success", { ns: "common" }),
      });
      setIsEditing(false);
    } catch (error) {
      handleApiError(error, toast, {
        title: t("config.toasts.updateFailedTitle"),
        defaultMessage: t("config.toasts.updateFailedMessage"),
      });
    }
  };

  const handleCancel = () => {
    setValue(config.value || "");
    setIsEditing(false);
  };

  const resolveOptionLabel = (option) => {
    const textValue =
      typeof option?.text === "string" ? option.text.trim() : "";
    const optionValue =
      typeof option?.value === "string" ? option.value.trim() : "";

    const translationCandidates = [...new Set([optionValue, textValue])]
      .filter((candidate) => candidate && isTechnicalLabel(candidate))
      .flatMap((candidate) => [candidate, candidate.toLowerCase()]);

    for (const candidate of translationCandidates) {
      const scopedKey = `config.optionLabels.${config.key}.${candidate}`;
      if (i18n.exists(scopedKey, { ns: "settings" })) {
        return t(scopedKey, { ns: "settings" });
      }

      const commonKey = `config.optionLabels.common.${candidate}`;
      if (i18n.exists(commonKey, { ns: "settings" })) {
        return t(commonKey, { ns: "settings" });
      }
    }

    if (textValue) {
      return isTechnicalLabel(textValue)
        ? humanizeTechnicalLabel(textValue)
        : textValue;
    }

    if (optionValue) {
      return isTechnicalLabel(optionValue)
        ? humanizeTechnicalLabel(optionValue)
        : optionValue;
    }

    return t("config.notSet");
  };

  const getDisplayText = () => {
    if (config.type === 2 && config.options) {
      const selectedOption = config.options.find(
        (option) => option.value === value,
      );
      if (selectedOption) {
        return resolveOptionLabel(selectedOption);
      }

      if (!value) {
        return t("config.notSet");
      }

      return isTechnicalLabel(value) ? humanizeTechnicalLabel(value) : value;
    }

    if (config.type === 3 && config.options) {
      if (!value) return t("config.notSet");
      const selectedValues = value.split("|").filter((v) => v !== "");
      if (selectedValues.length === 0) return t("config.notSet");

      const selectedTexts = selectedValues
        .map((val) => {
          const option = config.options.find((opt) => opt.value === val);
          if (option) {
            return resolveOptionLabel(option);
          }

          return isTechnicalLabel(val) ? humanizeTechnicalLabel(val) : val;
        })
        .filter(Boolean);

      return selectedTexts.length > 0
        ? selectedTexts.join(", ")
        : t("config.notSet");
    }

    return value || t("config.notSet");
  };

  const renderInput = () => {
    if (!isEditing) {
      return (
        <div className="flex w-full items-center justify-between">
          <div className="text-foreground min-w-0 flex-1 pe-2 text-sm break-words">
            {updateConfigMutation.isPending ? (
              <div
                className="flex items-center gap-2"
                role="status"
                aria-live="polite"
              >
                <Icon
                  name="Loader"
                  className="text-primary h-4 w-4 animate-spin motion-reduce:animate-none"
                />
                <span>{t("config.actions.updating")}</span>
              </div>
            ) : (
              getDisplayText()
            )}
          </div>
          <IconButton
            type="button"
            onClick={() => setIsEditing(true)}
            disabled={updateConfigMutation.isPending}
            emphasis="ghost"
            intent="neutral"
            size="iconTouch"
            aria-label={t("config.actions.editAria")}
          >
            <Icon name="Edit" className="h-4 w-4" />
          </IconButton>
        </div>
      );
    }

    if (config.type === 2 && config.options) {
      // Select dropdown
      return (
        <div className="flex w-full items-center space-x-2">
          <NativeSelect
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={updateConfigMutation.isPending}
            className="flex-1"
            aria-label={config.name}
          >
            {config.options.map((option) => (
              <option key={option.value} value={option.value}>
                {resolveOptionLabel(option)}
              </option>
            ))}
          </NativeSelect>
          <IconButton
            type="button"
            onClick={handleSave}
            disabled={updateConfigMutation.isPending}
            emphasis="solid"
            intent="success"
            size="iconTouch"
            aria-label={t("actions.save", { ns: "common" })}
          >
            {updateConfigMutation.isPending ? (
              <Icon
                name="Loader"
                className="h-4 w-4 animate-spin motion-reduce:animate-none"
              />
            ) : (
              <Icon name="Check" className="h-4 w-4" />
            )}
          </IconButton>
          <IconButton
            type="button"
            onClick={handleCancel}
            disabled={updateConfigMutation.isPending}
            emphasis="outline"
            intent="neutral"
            size="iconTouch"
            aria-label={t("actions.cancel", { ns: "common" })}
          >
            <Icon name="X" className="h-4 w-4" />
          </IconButton>
        </div>
      );
    } else if (config.type === 3 && config.options) {
      // Multi-select (checkboxes)
      const selectedValues = value
        ? value.split("|").filter((v) => v !== "")
        : [];

      const handleMultiSelectChange = (optionValue) => {
        const isSelected = selectedValues.includes(optionValue);
        let newValues;
        if (isSelected) {
          newValues = selectedValues.filter((v) => v !== optionValue);
        } else {
          newValues = [...selectedValues, optionValue];
        }
        setValue(newValues.join("|"));
      };

      return (
        <div className="w-full">
          <div className="mt-2 mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
            {config.options.map((option) => {
              const isSelected = selectedValues.includes(option.value);
              return (
                <label
                  key={option.value}
                  className={`relative flex items-center justify-between rounded-lg border p-4 transition-colors duration-200 motion-reduce:transition-none ${
                    isSelected
                      ? "border-primary bg-accent text-accent-foreground"
                      : "border-border bg-background hover:border-primary/50 hover:bg-accent/50"
                  } ${
                    updateConfigMutation.isPending
                      ? "cursor-not-allowed opacity-50"
                      : "cursor-pointer"
                  } `}
                >
                  {/* Option text */}
                  <div className="pr-8">
                    <span className="text-foreground text-sm font-medium">
                      {resolveOptionLabel(option)}
                    </span>
                  </div>
                  <Checkbox
                    checked={isSelected}
                    disabled={updateConfigMutation.isPending}
                    onChange={() => handleMultiSelectChange(option.value)}
                    aria-label={resolveOptionLabel(option)}
                  />
                </label>
              );
            })}
          </div>

          {/* Selected count indicator */}
          {selectedValues.length > 0 && (
            <div className="text-primary mb-3 text-xs font-medium">
              {selectedValues.length}{" "}
              {selectedValues.length === 1
                ? t("config.selection.item")
                : t("config.selection.items")}{" "}
              {t("config.selection.selected")}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex items-center justify-end space-x-2">
            <Button
              type="button"
              onClick={handleSave}
              disabled={updateConfigMutation.isPending}
              emphasis="solid"
              intent="success"
            >
              {updateConfigMutation.isPending ? (
                <div className="flex items-center space-x-2">
                  <Icon
                    name="Loader"
                    className="h-4 w-4 animate-spin motion-reduce:animate-none"
                  />
                  <span>{t("config.actions.saving")}</span>
                </div>
              ) : (
                <span className="flex items-center space-x-2">
                  <Icon name="Check" className="h-4 w-4" />
                  <span>{t("actions.save", { ns: "common" })}</span>
                </span>
              )}
            </Button>
            <Button
              type="button"
              onClick={handleCancel}
              disabled={updateConfigMutation.isPending}
              emphasis="outline"
              intent="neutral"
            >
              {t("actions.cancel", { ns: "common" })}
            </Button>
          </div>
        </div>
      );
    } else {
      // Text input
      const isNumericField = NUMERIC_FIELDS.includes(config.key);
      const charLimit = CHAR_LIMIT_FIELDS[config.key] || null;

      return (
        <div className="flex w-full items-center space-x-2">
          <Input
            type={isNumericField ? "number" : "text"}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={updateConfigMutation.isPending}
            className="flex-1"
            aria-label={config.name}
            placeholder={t("config.placeholders.enterValue")}
            min={isNumericField ? "0" : null}
            step={isNumericField ? "1" : null}
            maxLength={charLimit}
          />
          <IconButton
            type="button"
            onClick={handleSave}
            disabled={updateConfigMutation.isPending}
            emphasis="solid"
            intent="success"
            size="iconTouch"
            aria-label={t("actions.save", { ns: "common" })}
          >
            {updateConfigMutation.isPending ? (
              <Icon
                name="Loader"
                className="h-4 w-4 animate-spin motion-reduce:animate-none"
              />
            ) : (
              <Icon name="Check" className="h-4 w-4" />
            )}
          </IconButton>
          <IconButton
            type="button"
            onClick={handleCancel}
            disabled={updateConfigMutation.isPending}
            emphasis="outline"
            intent="neutral"
            size="iconTouch"
            aria-label={t("actions.cancel", { ns: "common" })}
          >
            <Icon name="X" className="h-4 w-4" />
          </IconButton>
        </div>
      );
    }
  };

  return (
    <div
      className="border-border bg-background rounded-lg border p-4"
      aria-busy={updateConfigMutation.isPending || undefined}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="mb-1 flex items-center space-x-2">
            <h4 className="text-foreground text-sm font-medium">
              {config.name}
            </h4>
          </div>
          {config.description && (
            <p className="text-muted-foreground mb-1 text-xs">
              {config.description}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-2">{renderInput()}</div>
    </div>
  );
};

export default SettingsConfig;
